import React, { useState, useMemo } from 'react';
import {
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Download,
  CheckCircle,
  HourglassEmpty,
  Cancel,
} from '@mui/icons-material';
import { formatCurrency } from './KPISummaryCards';

/**
 * Settlement status chip.
 */
const SettlementStatusChip = ({ status }) => {
  const cfg = {
    PENDING:    { label: 'Pending',    color: 'warning', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    PROCESSING: { label: 'Processing', color: 'info',    icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    COMPLETED:  { label: 'Completed',  color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    FAILED:     { label: 'Failed',     color: 'error',   icon: <Cancel sx={{ fontSize: 14 }} /> },
  };
  const c = cfg[(status || '').toUpperCase()] || { label: status || 'Unknown', color: 'default', icon: null };

  return (
    <Chip
      icon={c.icon}
      label={c.label}
      color={c.color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
    />
  );
};

/**
 * SettlementsTab
 *
 * Table of settlement waterfall results, with search and status filter.
 */
const SettlementsTab = ({ settlements = [], loading = false }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const statuses = ['ALL', 'PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'];

  const filtered = useMemo(() => {
    return settlements.filter((s) => {
      const matchSearch =
        !search ||
        (s.credit_case_id?.toString() || '').includes(search) ||
        (s.farmer_name || '').toLowerCase().includes(search.toLowerCase());
      const matchStatus =
        statusFilter === 'ALL' ||
        (s.status || '').toUpperCase() === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [settlements, search, statusFilter]);

  // Summary
  const totalFarmerPayout = settlements
    .filter((s) => (s.status || '').toUpperCase() === 'COMPLETED')
    .reduce((sum, s) => sum + (parseFloat(s.farmer_payout) || 0), 0);
  const totalDeductions = settlements
    .filter((s) => (s.status || '').toUpperCase() === 'COMPLETED')
    .reduce((sum, s) => sum + (parseFloat(s.total_deductions) || 0), 0);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-10 bg-gray-200 rounded w-full" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 bg-gray-100 rounded w-full" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <div className="rounded-xl bg-purple-50 border border-purple-100 p-4">
          <p className="text-xs text-purple-600 font-medium">Total Settlements</p>
          <p className="text-lg font-bold text-purple-800">{settlements.length}</p>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
          <p className="text-xs text-emerald-600 font-medium">Farmer Payouts</p>
          <p className="text-lg font-bold text-emerald-800">{formatCurrency(totalFarmerPayout)}</p>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
          <p className="text-xs text-amber-600 font-medium">Total Deductions</p>
          <p className="text-lg font-bold text-amber-800">{formatCurrency(totalDeductions)}</p>
        </div>
        <div className="rounded-xl bg-red-50 border border-red-100 p-4">
          <p className="text-xs text-red-600 font-medium">Pending</p>
          <p className="text-lg font-bold text-red-800">
            {settlements.filter((s) => (s.status || '').toUpperCase() === 'PENDING').length}
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <TextField
          placeholder="Search by case ID or farmer name..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: '#9ca3af' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />

        <div className="flex items-center gap-1 flex-wrap">
          <FilterList sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />
          {statuses.map((s) => (
            <Chip
              key={s}
              label={s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
              variant={statusFilter === s ? 'filled' : 'outlined'}
              color={statusFilter === s ? 'primary' : 'default'}
              size="small"
              onClick={() => setStatusFilter(s)}
              sx={{ fontWeight: 500, fontSize: '0.65rem', cursor: 'pointer' }}
            />
          ))}
        </div>

        <div className="ml-auto">
          <Tooltip title="Export Settlements CSV">
            <IconButton size="small">
              <Download sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-3">
        Showing {filtered.length} of {settlements.length} settlements
      </p>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Case ID</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Farmer</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Balance Before</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Deductions</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Farmer Payout</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-gray-400 text-sm">
                  No settlements found
                </td>
              </tr>
            ) : (
              filtered.map((s, idx) => (
                <tr key={s.id || idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">#{s.credit_case_id}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{s.farmer_name || 'N/A'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <SettlementStatusChip status={s.status} />
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatCurrency(parseFloat(s.balance_before) || 0)}</td>
                  <td className="px-4 py-3 text-red-600 font-medium">{formatCurrency(parseFloat(s.total_deductions) || 0)}</td>
                  <td className="px-4 py-3 text-emerald-600 font-bold">{formatCurrency(parseFloat(s.farmer_payout) || 0)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {s.created_at
                      ? new Date(s.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Tooltip title="View Waterfall">
                      <IconButton size="small">
                        <Visibility sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SettlementsTab;
